import { useGetAlbumVideo } from '@vimeo/gctl-api/hooks/useAlbumVideo';
import { VideoFields } from '@vimeo/gctl-api/models/components/schemas/video/Video';

import { useAlbumStore } from '../state/albumStore';
import { VIDEOS_API_VERSION } from '../utils/constants';

const DEFAULT_SHOWCASE_QUERY = {
  forceCollectionUri: true,
};

const SELECT_FIELDS = [
  'metadata.connections.likes.total',
  'metadata.interactions.like.added',
  'metadata.interactions.like.uri',
];

/**
 * Custom React hook to fetch and manage like data for a specific video (clip) within an album.
 *
 * @param clipId - The ID of the video (clip) to fetch data for, or `null` if not available.
 * @returns An object containing:
 *   - `data`: The fetched video data, or `undefined` if not loaded.
 *   - `isLoading`: Boolean indicating if the data is currently being loaded.
 *   - `error`: Any error encountered during the fetch operation.
 *   - `mutate`: Function to manually revalidate or update the fetched data.
 */
export const useGetAlbumVideoLikeData = (clipId: string | null) => {
  const { albumId, albumSignature, password, isShowcaseEmbedded, referrer } = useAlbumStore(
    (state) => ({
      albumId: state.albumId,
      albumSignature: state.albumSignature,
      password: state.hashedPass,
      isShowcaseEmbedded: state.isShowcaseEmbedded,
      referrer: state.referrer,
    }),
  );

  const embedQuery = {
    isEmbed: isShowcaseEmbedded,
    referrer,
  };

  const { data, isLoading, error, mutate } = useGetAlbumVideo(
    () => {
      if (!albumId || !clipId || albumSignature === null) return null;

      return {
        where: { albumId, videoId: Number(clipId) },
        select: SELECT_FIELDS as VideoFields[],
        query: {
          ...DEFAULT_SHOWCASE_QUERY,
          ...embedQuery,
          ...(password && { password }),
          ...(albumSignature && { albumSignature, albumUri: `albums/${albumId}` }),
        },
        headers: { Accept: `application/vnd.vimeo.albumvideo;version=${VIDEOS_API_VERSION}` },
      };
    },
    {
      revalidateOnFocus: false,
    },
  );

  return {
    data,
    isLoading,
    error,
    mutate,
  };
};
